import { useEffect, useMemo, useState } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { css } from '@emotion/react';

import { LinearProgress } from '@lego/klik-react';
import { Select, Table, Tag, Input } from '@lego/klik-ui';

import { media } from '../../../config';
import { Heading } from '../../Heading';
import { OverlayPortal } from '../../OverlayPortal';
import { Spinner } from '../../spinner/Spinner';
import {
  ACTIVATE_BUSINESS_UNIT_YEAR,
  GET_ELIGIBLE_ASSESSMENT_YEARS,
  UPDATE_BUSINESS_UNIT_DEADLINE_YEAR,
} from '../../../queries/assessmentAdmin';
import { generalApiErrorHandler } from '../../../utils/generalApiErrorHandler';
import { GenericConfirmationModal } from '../../../components/modals/GenericConfirmationModal';
import {
  addSnackbar,
  snackbarTypes,
  snackbarDefaultMessages,
} from '../../../utils/snackbar';
import { spinnerOverlayText } from '../../../styles/common';

const assessmentYearSelect = css({
  width: 150,
});

const progressBarContainer = css({
  [media.medium]: {
    width: 400,
  },
});

const ActivateYearAndBusinessUnit = () => {
  const [eligibleYears, setEligibleYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState();
  const [selectedBu, setSelectedBu] = useState('');
  const [activateConfirmModalVisibility, setActivateConfirmModalVisibility] =
    useState(false);

  const handleGetEligibleYearsResponse = (response) => {
    setEligibleYears(response.getEligibleAssessmentYears);
  };

  const [
    getEligibleYears,
    { loading: eligibleYearsLoading, refetch: refetchEligibleYears },
  ] = useLazyQuery(GET_ELIGIBLE_ASSESSMENT_YEARS, {
    fetchPolicy: 'network-only',
    onCompleted: handleGetEligibleYearsResponse,
  });

  const getAllBusinessUnitsForYear = useMemo(() => {
    return eligibleYears
      .filter((assessmentYear) => assessmentYear.year === selectedYear)
      .flatMap((assessmentYear) => assessmentYear.businessUnits);
  }, [eligibleYears, selectedYear]);

  const getEligibleYearOptions = () =>
    eligibleYears.map((assessmentYear) => assessmentYear.year);

  useEffect(() => {
    getEligibleYears();

    const handleCustomerDataUpdateEvent = () => {
      getEligibleYears();
    };

    document.addEventListener(
      'customerDataGenerated',
      handleCustomerDataUpdateEvent
    );
    return () => {
      document.removeEventListener(
        'customerDataGenerated',
        handleCustomerDataUpdateEvent
      );
    };
  }, [getEligibleYears]);

  if (eligibleYears.length > 0 && !selectedYear) {
    setSelectedYear(
      Math.max(...eligibleYears.map((assessmentYear) => assessmentYear.year))
    );
  }

  const showConfirmActivateModal = (buName) => {
    setSelectedBu(buName);
    setActivateConfirmModalVisibility(true);
  };

  const [activateBuForYear, { loading: activateBusinessUnitLoading }] =
    useMutation(ACTIVATE_BUSINESS_UNIT_YEAR, {
      onCompleted: handleActivateBuResponse,
    });

  const [
    updateBuDeadlineForYear,
    { loading: updateBusinessUnitDeadlineLoading },
  ] = useMutation(UPDATE_BUSINESS_UNIT_DEADLINE_YEAR, {
    onCompleted: (response) => {
      if (response.updateBusinessUnitDeadlineForYear) {
        addSnackbar({
          header: snackbarDefaultMessages.successHeader,
          text: 'Business Unit deadline has been updated',
          type: snackbarTypes.SUCCESS,
        });
      }
    },
  });

  async function handleBusinessUnitActivation() {
    try {
      await activateBuForYear({
        variables: { businessUnit: selectedBu, year: selectedYear },
      });
    } catch (error) {
      generalApiErrorHandler(error);
      setActivateConfirmModalVisibility(false);
    }
  }

  async function handleBusinessUnitUpdateDeadline(buName, deadline) {
    const deadlineDate = new Date(deadline);
    if (isNaN(deadlineDate.getTime())) {
      addSnackbar({
        header: snackbarDefaultMessages.errorHeader,
        text: 'Invalid deadline date. Please select a valid date.',
        type: snackbarTypes.ERROR,
      });
      return;
    }
    try {
      const utcDeadlineTimestamp = Date.UTC(
        deadlineDate.getUTCFullYear(),
        deadlineDate.getUTCMonth(),
        deadlineDate.getUTCDate()
      );
      await updateBuDeadlineForYear({
        variables: {
          businessUnit: buName,
          year: selectedYear,
          deadlineTimestamp: utcDeadlineTimestamp.toString(),
        },
      });
      handleGetEligibleYearsResponse((await refetchEligibleYears()).data);
    } catch (error) {
      generalApiErrorHandler(error);
    }
  }

  function handleActivateBuResponse(response) {
    if (response.activateBusinessUnitForYear) {
      const { year, businessUnits } = response.activateBusinessUnitForYear;

      let updatedEligibleYears = eligibleYears.map((eligibleYear) => {
        if (eligibleYear.year === year) {
          return {
            deadlineTimestamp: eligibleYear.deadlineTimestamp,
            year,
            businessUnits,
          };
        } else {
          return eligibleYear;
        }
      });

      setEligibleYears([...updatedEligibleYears]);

      setActivateConfirmModalVisibility(false);
      addSnackbar({
        header: snackbarDefaultMessages.successHeader,
        text: 'Business Unit has been activated',
        type: snackbarTypes.SUCCESS,
      });
    }
  }

  if (activateBusinessUnitLoading) {
    return (
      <OverlayPortal>
        <Spinner />
        <p css={spinnerOverlayText}>
          Activating the business unit. This may take a few minutes.
        </p>
      </OverlayPortal>
    );
  }

  return (
    <div>
      {activateConfirmModalVisibility && (
        <GenericConfirmationModal
          status="warning"
          header="Confirm activation"
          body={`Are you sure you want to activate the Business Unit: ${selectedBu} for year: ${selectedYear}? </br></br>
               This will activate all Long Tail assessments for ${selectedBu} and will allow customers to access the self-assessment scoring.`}
          onClose={() => setActivateConfirmModalVisibility(false)}
          onConfirm={handleBusinessUnitActivation}
        />
      )}
      <Heading type="h2" text="Active Year" />
      Determines which surveys are visible for long-tail customers and what MCP
      version will be flagged active.
      {eligibleYearsLoading ? (
        <div css={progressBarContainer}>
          <LinearProgress />{' '}
        </div>
      ) : (
        <div>
          <Heading type="h3" text="Eligible years" />
          {eligibleYears.length === 0 ? (
            <p data-testid="noResponseMessage">
              No eligible years are available. Please upload a customer/channel
              mapping first.
            </p>
          ) : (
            <div>
              <div css={assessmentYearSelect}>
                <Select
                  data-testid="eligibleYearSelect"
                  size="sm"
                  value={selectedYear}
                  onChange={(event) =>
                    setSelectedYear(parseInt(event.target.value))
                  }
                >
                  {getEligibleYearOptions().map((yearOption, i) => (
                    <option key={i}>{yearOption}</option>
                  ))}
                </Select>
              </div>
              <Table variant="simple" data-testid="eligibleBuTable">
                <Table.Head>
                  <Table.Row>
                    <Table.Cell
                      fontWeight="bold"
                      borderBottom="none"
                      paddingLeft={0}
                      paddingBottom={0}
                    >
                      Eligible Business Unit
                    </Table.Cell>
                    <Table.Cell
                      fontWeight="bold"
                      borderBottom="none"
                      paddingBottom={0}
                    >
                      Deadline
                    </Table.Cell>
                    <Table.Cell
                      fontWeight="bold"
                      borderBottom="none"
                      paddingBottom={0}
                    >
                      Active?
                    </Table.Cell>
                  </Table.Row>
                </Table.Head>
                <Table.Body data-testid="eligibleBuTableBody">
                  {getAllBusinessUnitsForYear.map((bu) => {
                    const deadlineDate = new Date(Number(bu.deadlineTimestamp));
                    return (
                      <Table.Row key={bu.businessUnitName}>
                        <Table.Cell borderBottom="none" paddingLeft={0}>
                          <Tag colorScheme="light-blue">
                            <Tag.Label>{bu.businessUnitName}</Tag.Label>
                          </Tag>
                        </Table.Cell>
                        <Table.Cell borderBottom="none">
                          <Input
                            type="date"
                            size="sm"
                            disabled={updateBusinessUnitDeadlineLoading}
                            value={
                              bu.deadlineTimestamp
                                ? deadlineDate.toISOString().split('T')[0]
                                : ''
                            }
                            onChange={(event) =>
                              handleBusinessUnitUpdateDeadline(
                                bu.businessUnitName,
                                event.target.value
                              )
                            }
                          />
                        </Table.Cell>
                        <Table.Cell borderBottom="none">
                          <div
                            className={`klik-switch ${
                              bu.active ? 'klik-is-active' : ''
                            } ${
                              bu.active || updateBusinessUnitDeadlineLoading
                                ? 'klik-is-disabled'
                                : ''
                            }`}
                            onClick={() => {
                              if (bu.deadlineTimestamp === undefined) {
                                addSnackbar({
                                  header: snackbarDefaultMessages.errorHeader,
                                  text: 'Please set a deadline for the Business Unit before activating it.',
                                  type: snackbarTypes.ERROR,
                                });
                                return;
                              }

                              showConfirmActivateModal(bu.businessUnitName);
                            }}
                          >
                            <div className={'klik-switch-dot'} />
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { ActivateYearAndBusinessUnit };
